import { observer } from "mobx-react";
import { InsuranceInformationBaseStore } from "../stores";
import { ConfirmationButton, Paper, PaperBorderType, StatusChip, StatusType } from "@roc/ui";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Divider, Link, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Theme, Tooltip, Typography, makeStyles } from "@material-ui/core";
import { CheckBox, Delete, ExpandMore } from "@material-ui/icons";

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    '& th': {
      borderBottom: '1px solid #eee',
    },
  },
  headerRow: {
    '& th': {
      verticalAlign: 'text-top',
      padding: '8px',
    },
    '& th:first-child': {
      paddingLeft: '16px',
    },
  },
  smallRow: {
    height: 32
  },
  smallCell: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  accordionDetails: {
    paddingTop: 0,
  },
  link: {
    cursor:'pointer',
  },
  fileName: {
  fontWeight: 500,
  },
  createdDate: {
    fontSize: '0.75rem',
    color: '#777',
    marginTop: '2px',
  }
}));

export type UnclassifiedDocumentsTableProps = {
  title: string;
  documents: any;
  emptyMessage: string;
  store: InsuranceInformationBaseStore;
  allowActions?:boolean;
  classifyOptions?:ClassifyOption[];
}
type ClassifyOption = {
  id: number;
  label: string;
}

export const UnclassifiedDocumentsTable = observer((
  {title, documents, emptyMessage, store, allowActions=true, classifyOptions = [] }: UnclassifiedDocumentsTableProps)=>{

    const handleChange = (documentId, classificationId) => {
      store.updateClassification(documentId, classificationId);
    };
    const classes = useStyles({});

    const getSelectedClassification = (classificationId) => {
      const classification = classifyOptions?.find((option)=>option.id === classificationId);
      return classification?.label || '';
    }

    return (
      <Paper borderType={PaperBorderType.SOLID_BORDER}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore/>}>
            <Typography variant="h5">{title}</Typography>
          </AccordionSummary>
          <Divider/>
          <AccordionDetails className={classes.accordionDetails}>
            <Table>
              <TableHead>
                <TableRow className={classes.headerRow}>
                  <TableCell>Document Name</TableCell>
                  <TableCell>Upload To</TableCell>
                  {allowActions && <TableCell style={{textAlign: 'center'}}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(documents) && documents.length == 0)?
                  <TableRow>
                    <TableCell colSpan={10}>
                      <Box>
                        {emptyMessage}
                      </Box>
                    </TableCell>
                  </TableRow>
                  :
                  documents?.map((document, index)=>(
                    <TableRow key={document.unclassifiedDocumentId} className={classes.smallRow}>
                      <TableCell className={classes.smallCell}>
                        <Box>
                          <Link
                            className={classes.link}
                            onClick={() =>
                              store.downloadUnclassifiedDocument(
                                document.loanId,
                                document.unclassifiedDocumentId,
                                document.fileName
                              )
                            }
                          >
                            <Typography variant="body2" className={classes.fileName}>
                              {document.fileName}
                            </Typography>
                          </Link>

                          <Typography variant="caption" className={classes.createdDate}>
                            {document.formattedCreatedDate}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell className={classes.smallCell}>
                        {allowActions ? <Select
                          variant="outlined"
                          value={document.classificationId ?? ''}
                          onChange={(e) => handleChange(document.unclassifiedDocumentId, e.target.value)}
                          displayEmpty
                          fullWidth
                        >
                          <MenuItem value="" disabled>
                            Select a Quote
                          </MenuItem>
                          {classifyOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        :
                        <StatusChip statusType={StatusType.UNCLASSIFIED_DOCS} label=    {document.classificationId ? getSelectedClassification(document?.classificationId): 'Unclassified'} variant={'outlined'}/>
                        }
                      </TableCell >
                      {allowActions && <TableCell className={classes.smallCell}>
                        <Box display="flex" justifyContent="center" gridGap={1}>
                          <Tooltip title="Assign">
                            <ConfirmationButton
                              testId="classifyDoc"
                              color="primary"
                              size="small"
                              disabled={!document.classificationId}
                              confirmDialogProps={{
                                body: <>
                                  <Typography>
                                    Are you sure you want to assign document <strong>{document.fileName}</strong> to <strong>{getSelectedClassification(document?.classificationId)}</strong>?
                                  </Typography>
                                </>,
                              }}
                              onClick={() => store.classifiedUnclassifiedDocument(document.unclassifiedDocumentId, document.classificationId)}
                            >
                              <CheckBox color="primary"/>
                            </ConfirmationButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <ConfirmationButton
                              testId="deleteDoc"
                              color="primary"
                              size="small"
                              confirmDialogProps={{
                                body: <>
                                  <Typography>
                                    Are you sure you want to delete document <strong>{document.fileName}</strong>?
                                  </Typography>
                                </>,
                              }}
                              onClick={() => store.deleteUnclassifiedDocument(document.unclassifiedDocumentId)}
                            >
                              <Delete color="primary"/>
                            </ConfirmationButton>
                          </Tooltip>
                        </Box>
                      </TableCell>}
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      </Paper>
    )
})